import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getSession, type NotificationItem } from '../lib/api.js';
import { asArray } from '../lib/as-array.js';
import { timeAgoShort } from '../lib/format.js';
import { usePoll } from '../lib/use-poll.js';

/** Broadcast so every mounted bell (and /alerts) reloads after a read/watch change. */
const CHANGED_EVENT = 'ma-notifications-changed';

type Notifications = Awaited<ReturnType<typeof api.notifications>>;

/**
 * TWO bells are always mounted: one in the sidebar and one in the mobile header. Neither unmounts -
 * they are hidden with CSS (a transform and `lg:hidden`) - so a per-instance poll fetched
 * /v1/notifications twice a minute, forever, for every signed-in user. This module-level
 * single-flight plus short TTL means the instances share one round-trip instead.
 */
let cache: { at: number; data: Notifications } | null = null;
let inflight: Promise<Notifications> | null = null;
const TTL_MS = 5_000;

function loadNotifications(): Promise<Notifications> {
  if (cache && Date.now() - cache.at < TTL_MS) return Promise.resolve(cache.data);
  inflight ??= api
    .notifications(12)
    .then((r) => {
      cache = { at: Date.now(), data: r };
      return r;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Drop the shared copy so the next read goes to the server (after a write, or on sign-out). */
function invalidateNotifications(): void {
  cache = null;
}

/**
 * Global notification bell (Tier 1 retention engine). Polls the unread count so a watch/LiveHunt hit
 * surfaces without a reload, and opens a dropdown of recent alerts. Signed-in users only.
 */
export function NotificationBell() {
  const signedIn = !!getSession().accessToken;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async (force = false) => {
    try {
      if (force) invalidateNotifications();
      const r = await loadNotifications();
      setItems(asArray(r.data));
      setUnread(r.unread);
    } catch {
      /* not signed in / transient */
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    // A change event means someone wrote; bypass the TTL so the badge is not one cycle stale.
    const onChanged = () => void load(true);
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(CHANGED_EVENT, onChanged);
    };
  }, [signedIn, load]);

  // usePoll owns the interval AND the visibility handling: this component previously refreshed on
  // becoming visible but kept its 60s timer running the whole time it was hidden - twice over, since
  // two bells are always mounted.
  usePoll(load, 60_000, { enabled: signedIn });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!signedIn) return null;

  const openPanel = () => {
    const willOpen = !open;
    if (willOpen && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const width = 320;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    }
    setOpen(willOpen);
    void load();
  };
  const markAll = async () => {
    await api.markNotificationsRead().catch(() => undefined);
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    // Without this the OTHER mounted bell kept rendering the stale unread badge for up to a
    // minute. /alerts already dispatches the same event after marking read.
    invalidateNotifications();
    window.dispatchEvent(new Event(CHANGED_EVENT));
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={btnRef}
        onClick={openPanel}
        aria-label="Notifications"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 text-white/80 hover:border-white/30 hover:text-white"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-cta px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 320 }}
          className="z-[70] max-h-[70vh] overflow-auto rounded-xl border border-border-default bg-bg-base shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
            <span className="text-sm font-semibold text-text-primary">Alerts</span>
            {unread > 0 && (
              <button className="text-xs text-accent-400 hover:underline" onClick={() => void markAll()}>Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-text-muted">
              No alerts yet. Watch a family/IOC or save a LiveHunt to get notified.
            </div>
          ) : (
            <div className="divide-y divide-border-subtle">
              {items.map((n) => (
                <Link
                  key={n.id}
                  to={n.link ?? '/alerts'}
                  onClick={() => setOpen(false)}
                  className={`block px-3 py-2 hover:bg-bg-surface ${n.read ? 'opacity-70' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${n.read ? 'bg-border-default' : 'bg-cta'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-text-primary">{n.title}</span>
                        <span className="shrink-0 text-[10px] text-text-muted">{timeAgoShort(n.createdAt)}</span>
                      </div>
                      <p className="truncate text-xs text-text-secondary">{n.body}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <Link to="/alerts" onClick={() => setOpen(false)} className="block border-t border-border-subtle px-3 py-2 text-center text-xs text-accent-400 hover:underline">
            See all alerts →
          </Link>
        </div>
      )}
    </div>
  );
}
